import React, { useEffect, useState } from "react";

/**
 * Floating "Install GreenBelt" prompt for PWA-supported browsers.
 *
 * - On Android / Chrome / Edge: catches the `beforeinstallprompt` event and
 *   shows a one-tap install button.
 * - On iOS Safari: shows a small instructions card ("Tap [share] → Add to Home Screen")
 *   since iOS doesn't fire `beforeinstallprompt`.
 * - Auto-hides once installed or if dismissed.
 *
 * Persistence: dismissal is remembered in localStorage for 14 days.
 */
const DISMISS_KEY = "gb_pwa_dismiss_until";
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

export default function InstallAppPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Hide if already installed (running standalone)
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // Honor 14-day dismissal
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (until && Date.now() < until) return;

    // Chromium-based
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS Safari fallback — show hint once
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (isIOS && isSafari) {
      // Delay so it doesn't fire on first paint
      const t = setTimeout(() => { setIosHint(true); setShow(true); }, 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onBefore); };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    setShow(false);
    if (choice?.outcome !== "accepted") {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
    }
  };

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-50 bg-ink text-bg border border-leaf/40 shadow-2xl animate-[fadeUp_0.4s_ease-out]"
    >
      <div className="p-4 flex items-start gap-3">
        <div className="w-10 h-10 flex-shrink-0 bg-leaf/20 border border-leaf/40 flex items-center justify-center font-mono text-leaf text-sm">GB</div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-leaf-light mb-1">Install app</div>
          <div className="font-serif text-base text-bg leading-snug">Add GreenBelt to your home screen</div>
          {iosHint ? (
            <div className="text-xs text-bg/70 mt-2 leading-relaxed">
              Tap the <span className="font-mono">Share</span> icon below, then <b>Add to Home Screen</b>. Opens like a native app, full-screen.
            </div>
          ) : (
            <div className="text-xs text-bg/70 mt-2 leading-relaxed">
              One-tap install — full screen, fast launch, offline-ready.
            </div>
          )}
          <div className="mt-3 flex gap-2">
            {!iosHint && (
              <button onClick={install} data-testid="pwa-install-btn"
                className="bg-leaf text-bg px-4 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-leaf-light hover:text-ink">
                Install
              </button>
            )}
            <button onClick={dismiss} data-testid="pwa-dismiss"
              className="text-bg/60 hover:text-bg px-2 py-1.5 text-xs font-mono uppercase tracking-wider underline underline-offset-4">
              {iosHint ? "Got it" : "Not now"}
            </button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Close" className="text-bg/50 hover:text-bg text-xl leading-none">×</button>
      </div>
      <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
